import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Modal,
  ModalContent,
  ModalBody,
  useDisclosure,
  Button,
} from '@heroui/react';
import styled from 'styled-components';
import JDDataStep from '@components/drive/content/tabs/apps/shared/jdDataStep';
import snackBarTypes from '../../../../../constants/snackBarTypes';
import WizardAnimation from '../../../../../utility/wizardAnimation';
import SnackBarAlerts from '../../../../common/snackBarAlerts/snackBarAlerts';
import UserDocumentsImageController from '../../../../../utility/userDocumentsImageController';
import getToken from '../../../../../utility/getToken';

import {
  GENERATE_CL_ERROR_MSG,
  GENERATE_CL_POPUP,
  JOB_SPECIFIC,
  WIZARD_STATUS_DEFAULT,
  WIZARD_STATUS_DONE,
} from './enums.generateCl';
import getWizardData from './getWizardData';
import { ButtonWrapper, TextSpan } from './ButtonWrapper';
import Logger from '../../../../../utility/services/Logger';
import GenerateCLPopup from './generateCLPopup/generateCLPopup';
import Actions from '../../../../../store/actions';
import { FormatTypes } from '../../../../../constants/templateDictionaries';
import TetraController from '../../../../../utility/TetraController';
import PopUpHeading from './jobSpecificCLPopup/data';

const HeadingText = styled.div`
  font-size: 20px;
  text-align: left;
`;

const ButtonsWrap = styled.div`
  display: flex;
  justify-content: end;
`;

const GenerateCoverLetter = (props) => {
  const { text, documentId, tetraData, userPreferences, universityPlan } =
    props;
  const [isPopupOpen, setIsPopupOpen] = useState(true);
  const [wizardData, setWizardData] = useState({});
  const [modal, setModal] = useState('');
  const [wizardAnimation, setWizardAnimation] = useState(false);

  const [snackBar, setSnackBar] = useState({
    showSnackBar: false,
    snackBarMessage: '',
    snackBarType: snackBarTypes.INFO,
    duration: null,
  });

  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const { heading } = PopUpHeading;

  const [presentData, setPresentData] = useState({
    title: '',
    location: '',
    description: '',
  });

  const [editorJDData, setEditorJDData] = useState({
    jobTitle: '',
    jobLocation: '',
    jobDescription: '',
    organizationName: '',
  });
  useEffect(() => {
    setEditorJDData((prevJDData) => ({
      ...prevJDData,
      jobTitle: presentData.title,
      jobLocation: presentData.location,
      jobDescription: presentData.description,
    }));
  }, [presentData]);

  const handleJDDataChange = (e) => {
    const { id, value } = e.target;
    setEditorJDData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const getJobSpecificCLData = async () => {
    const token = await getToken();
    const response = await UserDocumentsImageController.generateCoverLetter(
      token,
      {
        resumeId: documentId,

        jd: {
          title: editorJDData.jobTitle,
          location: editorJDData.jobLocation,
          description: editorJDData.jobDescription,
          organization: editorJDData.organizationName,
        },
        universityPlan,
      },
    );
    return response;
  };

  const handleCreate = async () => {
    onClose();
    setWizardAnimation(true);

    handleClosePopup();
    handleWizardData({
      text: 'Wizard at work: Crafting your Cover Letter from Resume...',
      animate: true,
      show: true,
      showTimer: true,
      buttons: [],
    });

    try {
      const res = await getJobSpecificCLData();
      const successWizardData = getWizardData({
        stateType: WIZARD_STATUS_DONE,
        handleWizardData,
        handleAutoGenerateCl,
        setWizardAnimation,
        res,
      });
      handleWizardData(successWizardData);
    } catch (e) {
      Logger.LogError(e);
      setWizardAnimation(false);
      setSnackBar({
        showSnackBar: true,
        snackBarMessage: GENERATE_CL_ERROR_MSG,
        snackBarType: snackBarTypes.ERROR,
      });

      const initialWizardValue = getWizardData({
        stateType: WIZARD_STATUS_DEFAULT,
        handleWizardData,
        handleAutoGenerateCl,
        setWizardAnimation,
      });
      handleWizardData(initialWizardValue);
    }
  };

  const getGeneratedClData = async () => {
    const token = await getToken();
    const response = await UserDocumentsImageController.generateCoverLetter(
      token,
      {
        resumeId: documentId,
        universityPlan,
      },
    );
    return response;
  };

  const handleWizardData = (data) => {
    setWizardData((prevState) => {
      return {
        ...prevState,
        ...data,
      };
    });
  };
  const handleAutoGenerateCl = async () => {
    try {
      const res = await getGeneratedClData();
      const successWizardData = getWizardData({
        stateType: WIZARD_STATUS_DONE,
        handleWizardData,
        handleAutoGenerateCl,
        setWizardAnimation,
        res,
      });
      handleWizardData(successWizardData);
    } catch (e) {
      Logger.LogError(e);
      setWizardAnimation(false);
      setSnackBar({
        showSnackBar: true,
        snackBarMessage: GENERATE_CL_ERROR_MSG,
        snackBarType: snackBarTypes.ERROR,
      });
    }
  };

  useEffect(() => {
    const initialWizardValue = getWizardData({
      stateType: WIZARD_STATUS_DEFAULT,
      handleWizardData,
      handleAutoGenerateCl,
      setWizardAnimation,
    });
    setWizardData(initialWizardValue);
  }, []);

  const getTetraData = async () => {
    const token = await getToken();
    const res = await TetraController.getTetraData(token, documentId);
    return res;
  };

  useEffect(() => {
    let isMounted = isPopupOpen; // Track whether the component is mounted
    const fetchData = async () => {
      let title = '';
      let location = '';
      let description = '';

      if (
        userPreferences?.layout?.tetraScan &&
        tetraData?.jdId &&
        tetraData?.jd
      ) {
        ({ title, location, description } = tetraData.jd);
      } else {
        const res = await getTetraData();

        if (res && res?.jdId) {
          ({ title, location, description } = res.jd);
        }
      }

      const initialCLData = { title, location, description };

      // Only update state if the component is still mounted
      setPresentData((data) => ({ ...data, ...initialCLData }));
    };

    if (isMounted) {
      fetchData();
    }

    return () => {
      isMounted = false; // Clean up: set isMounted to false when component unmounts
    };
  }, [isOpen]);
  const handleCloseSnackBar = (e) => {
    if (e) e.stopPropagation();
    setSnackBar({ showSnackBar: false });
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };
  const closeWizardAnimation = () => {
    setWizardAnimation(false);
  };
  const handleBackButton = () => {
    setModal(GENERATE_CL_POPUP);
  };

  const { showSnackBar, snackBarMessage, snackBarType, duration } = snackBar;
  const isSaveDisabled = Object.values(editorJDData).every(
    (value) => value.trim() !== '',
  );

  const setJobData = (data) => {
    const { jobTitle, location, completeJobDescription, company } = data;
    setEditorJDData({
      jobTitle,
      jobLocation: location,
      jobDescription: completeJobDescription,
      organizationName: company,
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="xl"
        className="py-3"
      >
        <ModalContent>
          <>
            <ModalBody>
              {modal === GENERATE_CL_POPUP && (
                <GenerateCLPopup
                  setWizardAnimation={setWizardAnimation}
                  handleClosePopup={handleClosePopup}
                  setModal={setModal}
                  onClose={onClose}
                />
              )}
              {modal === JOB_SPECIFIC && (
                <>
                  <HeadingText>{heading}</HeadingText>
                  <JDDataStep
                    handleJDDataChange={handleJDDataChange}
                    jobTitle={editorJDData.jobTitle}
                    jobLocation={editorJDData.jobLocation}
                    jobDescription={editorJDData.jobDescription}
                    organizationName={editorJDData.organizationName}
                    setJobData={setJobData}
                    isShowOrganization
                  />
                  <ButtonsWrap>
                    <Button
                      onClick={handleBackButton}
                      radius="full"
                      className="min-w-16 h-8 text-xs mr-3"
                    >
                      Back
                    </Button>
                    <Button
                      color="primary"
                      onClick={handleCreate}
                      isDisabled={!isSaveDisabled}
                      radius="full"
                      className="min-w-16 h-8 text-xs"
                    >
                      Create
                    </Button>
                  </ButtonsWrap>
                </>
              )}
            </ModalBody>
          </>
        </ModalContent>
      </Modal>
      <ButtonWrapper
        onClick={() => {
          onOpen();
          setModal(GENERATE_CL_POPUP);
        }}
        aria-label="AI cover letter"
      >
        {showSnackBar && (
          <SnackBarAlerts
            showSnackBar={showSnackBar}
            handleClose={handleCloseSnackBar}
            snackBarMessage={snackBarMessage}
            snackBarType={snackBarType}
            duration={duration}
          />
        )}
        {wizardAnimation && (
          <WizardAnimation
            wizardData={wizardData}
            closeWizardAnimation={closeWizardAnimation}
          />
        )}
        <TextSpan>{text}</TextSpan>
      </ButtonWrapper>
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { formatType } = ownProps;
  const common = {
    documentId: state.editResumeFormReducer.documentId,
    universityPlan: state.userInfoReducer.universityPlan,
  };
  if (formatType === FormatTypes.RESUME) {
    return {
      tetraData: state.tetraReducer.tetraData.AIJobSpecific,
      userPreferences: state.editResumeFormReducer.userPreferences,
      ...common,
    };
  }
  return {
    ...common,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    updateTetraData: (params) =>
      dispatch(Actions.tetraActions.updateTetraData(params)),
  };
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(GenerateCoverLetter);
